const nonEmptyText = (value) => typeof value === "string" && value.trim() ? value.trim() : null;

export const destinationMediaAssets = (destination) => {
  const candidates = [
    { src: nonEmptyText(destination?.hero_image), role: "hero" },
    { src: nonEmptyText(destination?.image), role: "image" },
  ].filter(({ src }) => Boolean(src));

  const seen = new Set();
  return candidates.filter(({ src }) => {
    if (seen.has(src)) return false;
    seen.add(src);
    return true;
  });
};

export const primaryDestinationMedia = (destination) => destinationMediaAssets(destination)[0] ?? null;

const textOrUnavailable = (value) => nonEmptyText(value) ?? "Not available";

const canonicalLabel = (value) => nonEmptyText(value)?.toUpperCase().replaceAll("_", " ") ?? null;

const legalAccessLabel = (visitInfo) => {
  const status = visitInfo?.legal_access?.status;
  const labels = {
    PUBLIC: "Public / legal access",
    MEMBERS_ONLY: "Members only",
    MEMBERS_OR_GUESTS: "Members or guest pass",
    RESTRICTED: "Restricted access",
    UNKNOWN: "Legal access not verified",
  };
  return labels[status] ?? (canonicalLabel(status) || "Legal access not verified");
};

const transportLabel = (value) => {
  const labels = {
    YES: "Yes",
    NO: "No",
    RESTRICTED: "Restricted",
    UNKNOWN: "Not verified",
    NOT_APPLICABLE: "Not applicable",
  };
  return labels[value] ?? (canonicalLabel(value) || "Not verified");
};

const uphillAccessLabel = (visitInfo, fallbackAccessLabel) => {
  const uphill = visitInfo?.uphill_access;
  if (!uphill) return fallbackAccessLabel || "Not available";

  const service = nonEmptyText(uphill.service_name);
  const kindLabels = { LIFT: "Lift", SHUTTLE: "Shuttle", NONE: "No uplift", UNKNOWN: "Uplift not verified" };
  const kind = service || kindLabels[uphill.uplift_kind] || canonicalLabel(uphill.uplift_kind) || "Uplift not verified";

  switch (uphill.uplift_relationship) {
    case "SERVES_TRAILS":
      return `${kind} · serves trail starts`;
    case "NEARBY_NOT_VERIFIED":
      return `${kind} nearby · trail uplift not verified`;
    case "NOT_APPLICABLE":
      return uphill.pedal_access === "YES" ? "Pedal access · no uplift" : "No uplift";
    default:
      return uphill.uplift_kind === "NONE"
        ? (uphill.pedal_access === "YES" ? "Pedal access · no uplift" : "No uplift")
        : `${kind} · trail uplift not verified`;
  }
};

const entryPointItem = (destination, fallbackLabel) => {
  const entry = destination?.visit_info?.entry_point;
  const label = nonEmptyText(entry?.label);
  if (!label) return { label: "Primary access point", value: fallbackLabel };

  const confidence = entry?.confidence && entry.confidence !== "VERIFIED"
    ? ` · ${String(entry.confidence).toLowerCase()}`
    : "";
  return {
    label: "Entry point",
    value: `${label}${confidence}`,
    href: nonEmptyText(entry?.source_url),
  };
};


export const practicalInfoGroups = (destination, { accessPointLabel = "Not available", accessLabel = "Not available" } = {}) => {
  const visit = destination?.visit_info;
  const legal = visit?.legal_access;
  const uphill = visit?.uphill_access;
  const entry = entryPointItem(destination, accessPointLabel);

  return [
    {
      id: "getting-there",
      title: "Getting there",
      items: [
        entry,
        { label: "Parking", value: textOrUnavailable(destination?.parking) },
        { label: "Legal access", value: visit ? legalAccessLabel(visit) : "Not available", href: nonEmptyText(legal?.source_url) },
        { label: "Uphill access", value: uphillAccessLabel(visit, accessLabel), href: nonEmptyText(uphill?.source_url) },
        ...(visit ? [{ label: "Pedal access", value: transportLabel(uphill?.pedal_access) }] : []),
        ...(visit && uphill?.uplift_relationship === "SERVES_TRAILS"
          ? [
              { label: "Bike transport", value: transportLabel(uphill?.bike_transport) },
              { label: "E-bike transport", value: transportLabel(uphill?.e_bike_transport) },
            ]
          : []),
      ],
    },
    {
      id: "plan-your-visit",
      title: "Plan your visit",
      items: [
        { label: "Season", value: textOrUnavailable(destination?.season) },
        { label: "Day ticket", value: textOrUnavailable(destination?.day_ticket_price) },
        { label: "Pricing notes", value: textOrUnavailable(destination?.pricing_notes) },
        ...(visit?.pricing_url ? [{ label: "Official prices", value: "Open official pricing", href: visit.pricing_url }] : []),
        ...(visit?.trail_status_url ? [{ label: "Trail status", value: "Check official trail status", href: visit.trail_status_url }] : []),
        ...(visit?.last_verified_at ? [{ label: "Visit info verified", value: visit.last_verified_at }] : []),
        { label: "Website", value: textOrUnavailable(destination?.website), href: nonEmptyText(destination?.website) },
      ],
    },
  ];
};


const legalDecision = (visitInfo) => {
  const status = visitInfo?.legal_access?.status;
  const map = {
    PUBLIC: { value: "Public access", tone: "good" },
    MEMBERS_ONLY: { value: "Members only", tone: "warn" },
    MEMBERS_OR_GUESTS: { value: "Guest pass", tone: "warn" },
    RESTRICTED: { value: "Restricted", tone: "alert" },
    UNKNOWN: { value: "Not verified", tone: "muted" },
  };
  return map[status] ?? { value: "Not verified", tone: "muted" };
};

const upliftDecision = (visitInfo, fallbackAccessLabel) => {
  const uphill = visitInfo?.uphill_access;
  if (!uphill) {
    return {
      value: fallbackAccessLabel || "Not verified",
      detail: fallbackAccessLabel ? "Legacy access data" : "No verified uplift data",
      tone: fallbackAccessLabel ? "neutral" : "muted",
    };
  }

  const kind = uphill.uplift_kind === "SHUTTLE" ? "Shuttle" : uphill.uplift_kind === "LIFT" ? "Lift" : null;
  if (uphill.uplift_relationship === "SERVES_TRAILS") {
    return {
      value: (kind || "Uplift") + " to trails",
      detail: nonEmptyText(uphill.service_name) || "Configured trail uplift capability",
      tone: "good",
    };
  }
  if (uphill.uplift_relationship === "NEARBY_NOT_VERIFIED") {
    return {
      value: (kind || "Lift") + " nearby",
      detail: "Not verified for trail uplift",
      tone: "warn",
    };
  }
  if (uphill.uplift_relationship === "NOT_APPLICABLE" || uphill.uplift_kind === "NONE") {
    return uphill.pedal_access === "YES"
      ? { value: "Pedal only", detail: "No mechanical uplift", tone: "neutral" }
      : { value: "No uplift", detail: "No verified uplift service", tone: "muted" };
  }
  if (uphill.pedal_access === "YES") {
    return { value: "Pedal access", detail: "Uplift not verified", tone: "neutral" };
  }
  return {
    value: kind ? kind + " unverified" : "Not verified",
    detail: "Trail uplift relationship unknown",
    tone: "muted",
  };
};

const ebikeDecision = (visitInfo) => {
  const uphill = visitInfo?.uphill_access;
  if (!uphill || uphill.uplift_relationship !== "SERVES_TRAILS") {
    return { value: "Not applicable", detail: "No verified trail uplift", tone: "muted" };
  }
  const map = {
    YES: { value: "Transport yes", detail: "E-bikes accepted", tone: "good" },
    RESTRICTED: { value: "Restricted", detail: "Check operator rules", tone: "warn" },
    NO: { value: "Not accepted", detail: "No e-bike uplift", tone: "alert" },
    UNKNOWN: { value: "Not verified", detail: "Check before travel", tone: "muted" },
    NOT_APPLICABLE: { value: "Not applicable", detail: "No e-bike uplift", tone: "muted" },
  };
  return map[uphill.e_bike_transport] ?? { value: "Not verified", detail: "Check before travel", tone: "muted" };
};

const destinationStatusDecision = (destination) => {
  const current = String(destination?.current_status || "UNKNOWN").toUpperCase();
  if (current === "OPEN_NOW") return { value: "Open now", tone: "good" };
  if (current === "CLOSED_NOW") return { value: "Closed now", tone: "alert" };
  if (current === "RESTRICTED") return { value: "Restricted", tone: "warn" };
  if (destination?.visit_info?.trail_status_url) return { value: "Check trail status", tone: "neutral" };
  return { value: "Hours not verified", tone: "muted" };
};

export const destinationDecisionSnapshot = (destination, { accessLabel = null } = {}) => {
  const visit = destination?.visit_info;
  return {
    status: {
      ...destinationStatusDecision(destination),
      href: nonEmptyText(visit?.trail_status_url),
    },
    legal: {
      ...legalDecision(visit),
      href: nonEmptyText(visit?.legal_access?.source_url),
    },
    uplift: {
      ...upliftDecision(visit, accessLabel),
      href: nonEmptyText(visit?.uphill_access?.source_url),
    },
    ebike: ebikeDecision(visit),
    season: {
      value: textOrUnavailable(destination?.season),
      tone: nonEmptyText(destination?.season) ? "neutral" : "muted",
    },
    hours: {
      value: textOrUnavailable(destination?.opening_hours),
      tone: nonEmptyText(destination?.opening_hours) ? "neutral" : "muted",
    },
    price: {
      value: nonEmptyText(destination?.day_ticket_price) || (visit?.pricing_url ? "Official pricing" : "Not available"),
      tone: nonEmptyText(destination?.day_ticket_price) || visit?.pricing_url ? "neutral" : "muted",
      href: nonEmptyText(visit?.pricing_url),
    },
    verifiedAt: nonEmptyText(visit?.last_verified_at),
  };
};